// gtRoutes — one place for the app's URL shapes and the Back-target rules.
//
// Why this exists: record pages are reachable from more than one parent. A role
// opened from the roles list must go back to the roles list; the same role opened
// from inside a client must go back to that client. Before the router this was
// solved by rendering the record as an in-place overlay and passing an onBack
// callback. With real URLs the parent is knowable from the path itself, so the
// rule lives here rather than being repeated in every screen.

export const NAV_PATHS = {
  Home: '/home',
  Roles: '/roles',
  Benches: '/benches',
  Clients: '/clients',
  Candidates: '/candidates',
  Intake: '/intake',
  Matches: '/matches',
  Interviews: '/interviews',
  Forms: '/forms',
  Sequences: '/sequences',
  'Job families': '/job-families', // C1-JOBFAM2
  Insights: '/insights', // C1-INSIGHTS2
  'Auto outreach': '/outreach', // C1-OUTREACH
  Inbox: '/inbox',
  Delivery: '/delivery',
  Settings: '/settings',
};

// Reverse lookup: which nav item should be highlighted for a given path.
// Longest prefix wins so /clients/4/roles/12 highlights Clients, not Roles.
export function navItemForPath(pathname) {
  let best = null;
  let bestLen = 0;
  for (const [name, base] of Object.entries(NAV_PATHS)) {
    if ((pathname === base || pathname.startsWith(base + '/')) && base.length > bestLen) {
      best = name;
      bestLen = base.length;
    }
  }
  return best;
}

export const paths = {
  home: () => '/home',
  candidates: () => '/candidates',
  candidate: (id) => `/candidates/${id}`,
  roles: () => '/roles',
  role: (id) => `/roles/${id}`,
  clients: () => '/clients',
  client: (id) => `/clients/${id}`,
  clientRole: (clientId, roleId) => `/clients/${clientId}/roles/${roleId}`,
  roleCandidate: (roleId, candidateId) => `/roles/${roleId}/candidates/${candidateId}`,
  clientRoleCandidate: (clientId, roleId, candidateId) =>
    `/clients/${clientId}/roles/${roleId}/candidates/${candidateId}`,
};

// The parent of the current path, and the label the Back button should carry.
// Nesting is what preserves the behaviour Jimmy asked for: Back follows the
// route you actually arrived through.
export function backTarget(pathname) {
  const p = pathname.replace(/\/+$/, '');

  let m = p.match(/^\/clients\/(\d+)\/roles\/(\d+)\/candidates\/\d+$/);
  if (m) return { to: paths.clientRole(m[1], m[2]), label: 'Role' };

  m = p.match(/^\/roles\/(\d+)\/candidates\/\d+$/);
  if (m) return { to: paths.role(m[1]), label: 'Role' };

  m = p.match(/^\/clients\/(\d+)\/roles\/\d+$/);
  if (m) return { to: paths.client(m[1]), label: 'Client' };

  if (/^\/candidates\/\d+$/.test(p)) return { to: paths.candidates(), label: 'Candidates' };
  if (/^\/roles\/\d+$/.test(p)) return { to: paths.roles(), label: 'Roles' };
  if (/^\/clients\/\d+$/.test(p)) return { to: paths.clients(), label: 'Clients' };

  return { to: paths.candidates(), label: 'Candidates' };
}
